/**
 * Lucky Bijoux — Theme JS
 * Vanilla ES6+. No framework dependencies.
 * Handles: Cart, Search, Announcement Bar, Carousels,
 *          Fade-in, Mobile Menu, Promo Popup, Tracking
 */

/* ============================================================
   DEVELOPER CONSOLE WATERMARK
   Edit: DEV.name, DEV.url, DEV.contact
   ============================================================ */
const DEV = {
  name:    'GM - Solutions Hub',
  url:     'https://gostyle.com.br',
  contact: 'gostyle.com.br',
};
console.log(
  '%c  ✦ Built by ' + DEV.name + '  ',
  'background: linear-gradient(135deg, #8c6b38, #c9a96e); color: #fff; font-family: Georgia, serif; font-size: 14px; font-weight: bold; padding: 10px 20px; border-radius: 4px; letter-spacing: 0.05em;'
);
console.log(
  '%c  ' + DEV.url + '  ',
  'color: #8c6b38; font-family: monospace; font-size: 11px; padding: 2px 0; letter-spacing: 0.1em;'
);
console.log(
  '%c  Curioso sobre o código? Fale com a gente 👋  ',
  'color: #aaa; font-family: monospace; font-size: 10px; padding: 0;'
);

/* ============================================================
   UTILITIES
   ============================================================ */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, ev, fn, opts) => el?.addEventListener(ev, fn, opts);
const off = (el, ev, fn) => el?.removeEventListener(ev, fn);

/* ============================================================
   LUCKY TRACKING — multi-platform event dispatcher
   Fires events to: GTM dataLayer + GA4 direct + Google Ads + Meta Pixel + TikTok
   Config injected by snippets/tracking.liquid → window.LuckyTrackingConfig
   ============================================================ */
const LuckyTracking = (() => {
  const cfg = () => window.LuckyTrackingConfig || {};
  const dl  = (obj) => { if (window.dataLayer) window.dataLayer.push(obj); };
  const ga  = (...args) => { if (typeof gtag === 'function') gtag(...args); };
  const fb  = (...args) => { if (typeof fbq  === 'function') fbq(...args); };
  const tt  = (...args) => { if (typeof ttq  !== 'undefined') ttq(...args); };

  function _gaItems(items) {
    return (items || []).map((i) => ({
      item_id:      String(i.variant_id || i.id || ''),
      item_name:    i.product_title || i.title || '',
      item_variant: i.variant_title || '',
      price:        (i.price || 0) / 100,
      quantity:     i.quantity || 1
    }));
  }

  return {
    addToCart(item) {
      const c = cfg();
      const value = (item.price || 0) / 100;
      const gaItems = _gaItems([item]);

      // GTM dataLayer (processed by GTM → GA4/Ads tags inside GTM)
      dl({ ecommerce: null });
      dl({ event: 'add_to_cart', ecommerce: { currency: 'BRL', value, items: gaItems } });

      // GA4 direct
      if (c.ga4Id) ga('event', 'add_to_cart', { currency: 'BRL', value, items: gaItems });

      // Google Ads add_to_cart conversion
      if (c.gadsId && c.gadsCart) {
        ga('event', 'conversion', { send_to: `${c.gadsId}/${c.gadsCart}`, value, currency: 'BRL' });
      }

      // Meta Pixel
      fb('track', 'AddToCart', {
        content_ids:  [String(item.variant_id || '')],
        content_type: 'product',
        value,
        currency: 'BRL'
      });

      // TikTok Pixel
      tt('track', 'AddToCart', { value, currency: 'BRL', content_id: String(item.variant_id || '') });
    },

    beginCheckout(cart) {
      const c = cfg();
      const value = (cart.total_price || 0) / 100;
      const gaItems = _gaItems(cart.items || []);
      const contentIds = (cart.items || []).map((i) => String(i.variant_id || ''));

      // GTM dataLayer
      dl({ ecommerce: null });
      dl({ event: 'begin_checkout', ecommerce: { currency: 'BRL', value, items: gaItems } });

      // GA4 direct
      if (c.ga4Id) ga('event', 'begin_checkout', { currency: 'BRL', value, items: gaItems });

      // Meta Pixel
      fb('track', 'InitiateCheckout', {
        content_ids:  contentIds,
        value,
        currency:     'BRL',
        num_items:    cart.item_count || 0
      });

      // TikTok Pixel
      tt('track', 'InitiateCheckout', { value, currency: 'BRL', content_ids: contentIds });
    },

    viewCart(cart) {
      const c = cfg();
      const value = (cart.total_price || 0) / 100;
      const gaItems = _gaItems(cart.items || []);

      // GTM dataLayer
      dl({ ecommerce: null });
      dl({ event: 'view_cart', ecommerce: { currency: 'BRL', value, items: gaItems } });

      // GA4 direct
      if (c.ga4Id) ga('event', 'view_cart', { currency: 'BRL', value, items: gaItems });
    },

    removeFromCart(item) {
      const c = cfg();
      const value = (item.price || 0) / 100;
      const gaItems = _gaItems([item]);
      dl({ ecommerce: null });
      dl({ event: 'remove_from_cart', ecommerce: { currency: 'BRL', value, items: gaItems } });
      if (c.ga4Id) ga('event', 'remove_from_cart', { currency: 'BRL', value, items: gaItems });
    },

    purchase(order) {
      // Chamado pela página de confirmação do Yampi via postMessage ou pixel de confirmação
      const c = cfg();
      const value = (order.total || 0);
      const gaItems = _gaItems(order.items || []);

      // GTM dataLayer
      dl({ ecommerce: null });
      dl({
        event: 'purchase',
        ecommerce: {
          transaction_id: String(order.id || ''),
          currency: 'BRL',
          value,
          items: gaItems
        }
      });

      // GA4 direct
      if (c.ga4Id) ga('event', 'purchase', {
        transaction_id: String(order.id || ''),
        currency: 'BRL',
        value,
        items: gaItems
      });

      // Google Ads purchase conversion
      if (c.gadsId && c.gadsPurchase) {
        ga('event', 'conversion', {
          send_to:        `${c.gadsId}/${c.gadsPurchase}`,
          value,
          currency:       'BRL',
          transaction_id: String(order.id || '')
        });
      }

      // Meta Pixel
      fb('track', 'Purchase', {
        content_ids:  (order.items || []).map((i) => String(i.variant_id || '')),
        content_type: 'product',
        value,
        currency: 'BRL'
      });

      // TikTok Pixel
      tt('track', 'CompletePayment', { value, currency: 'BRL' });
    }
  };
})();

function formatMoney(cents) {
  const amount = typeof cents === 'string' ? parseFloat(cents) : cents;
  const val = isNaN(amount) ? 0 : amount;
  return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatMoneyFromString(str) {
  // Shopify returns prices in the store currency
  const n = parseFloat(str);
  if (isNaN(n)) return str;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getInstallments(priceStr) {
  const price = parseFloat(priceStr);
  if (isNaN(price) || price <= 0) return '';
  const cfg = window.themeSettings && window.themeSettings.installments;
  const count = (cfg && cfg.count > 1) ? cfg.count : 6;
  const suffix = (cfg && cfg.suffix) ? cfg.suffix : ' sem juros';
  const installment = price / count;
  return `ou ${count}x de ${installment.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}${suffix}`;
}

/* ============================================================
   FADE-IN SECTION (IntersectionObserver)
   ============================================================ */
function initFadeInSections() {
  // Auto-apply [data-reveal] with stagger to grid/list children
  const staggerGroups = [
    { grid: '.trust-badges__grid',       item: '.trust-badge',            variant: ''      },
    { grid: '.care-guide__grid',         item: '.care-guide__card',       variant: ''      },
    { grid: '.seasonal__grid',           item: '.seasonal-card',          variant: 'scale' },
    { grid: '.instagram-grid',           item: '.instagram-post',         variant: 'scale' },
    { grid: '.product-grid',             item: '.product-card',           variant: 'scale' },
    { grid: '.collections-grid',         item: '.collection-card',        variant: 'scale' },
    { grid: '.customer-orders__list',    item: '.customer-order-card',    variant: ''      },
    { grid: '.customer-address__grid',   item: '.customer-address-card',  variant: ''      },
    { grid: '.faq__list',                item: '.faq__item',              variant: ''      },
  ];

  staggerGroups.forEach(({ grid, item, variant }) => {
    document.querySelectorAll(grid).forEach((parent) => {
      let i = 0;
      parent.querySelectorAll(item).forEach((el) => {
        if (el.hasAttribute('data-reveal')) return;
        el.setAttribute('data-reveal', variant);
        el.style.setProperty('--reveal-delay', `${Math.min(i * 0.09, 0.45)}s`);
        i++;
      });
    });
  });

  // Directional reveals for section eyebrows, titles and links
  const directional = [
    { sel: '.product-carousel__subtitle, .related-products__eyebrow, .seasonal__eyebrow, .section-eyebrow', variant: 'left' },
    { sel: '.product-carousel__viewall, .related-products__viewall', variant: 'right' },
  ];
  directional.forEach(({ sel, variant }) => {
    document.querySelectorAll(sel).forEach((el) => {
      if (!el.hasAttribute('data-reveal')) el.setAttribute('data-reveal', variant);
    });
  });

  // Observe everything
  const allEls = $$('.fade-in-section, [data-reveal]');
  if (!allEls.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // one-shot: never hide again after reveal
        }
      });
    },
    { threshold: 0.06, rootMargin: '0px 0px -32px 0px' }
  );

  allEls.forEach((el) => observer.observe(el));
}

/* ============================================================
   ANNOUNCEMENT BAR
   ============================================================ */

/* ============================================================
   MOBILE MENU
   ============================================================ */
function initMobileMenu() {
  const overlay = $('#mobile-menu-overlay');
  const openBtn = $('#mobile-menu-open');
  const closeBtn = $('#mobile-menu-close');
  const backdrop = $('#mobile-menu-backdrop');

  if (!overlay) return;

  // The header section inline script handles display:none/flex toggle.
  // theme.js only needs to manage body overflow and sub-menus.
  const open = () => {
    overlay.style.display = 'flex';
    setTimeout(() => overlay.classList.add('is-open'), 10);
    document.body.style.overflow = 'hidden';
  };
  const close = () => {
    overlay.classList.remove('is-open');
    setTimeout(() => { overlay.style.display = 'none'; }, 300);
    document.body.style.overflow = '';
  };

  on(openBtn, 'click', open);
  on(closeBtn, 'click', close);
  on(backdrop, 'click', close);

  // Sub-menus
  $$('.mobile-nav-item__chevron', overlay).forEach((btn) => {
    on(btn, 'click', () => {
      const subs = btn.closest('.mobile-nav-item').querySelector('.mobile-nav-subitems');
      const isOpen = subs?.classList.contains('is-open');
      // close all
      $$('.mobile-nav-subitems.is-open', overlay).forEach((s) => s.classList.remove('is-open'));
      $$('.mobile-nav-item__chevron.is-open', overlay).forEach((b) => b.classList.remove('is-open'));
      if (!isOpen && subs) {
        subs.classList.add('is-open');
        btn.classList.add('is-open');
      }
    });
  });
}

/* ============================================================
   EMBLA CAROUSEL — lightweight wrapper
   ============================================================ */
// We inline a minimal Embla-compatible scroll carousel using
// native scroll + snapping for maximum compatibility.
/* ============================================================
   FOOTER ACCORDION — mobile only
   ============================================================ */
/* ============================================================
   STICKY HEADER — oculta ao descer, reaparece ao subir
   ============================================================ */
function initStickyHeader() {
  const siteTop = $('.site-top');
  const header = $('[data-sticky]');
  if (!siteTop || !header) return;
  if (header.dataset.sticky !== 'true') return;

  let lastY = window.scrollY;
  let ticking = false;
  let accDelta = 0;          // accumulated scroll delta
  const HIDE_THRESHOLD = 60; // px downward to trigger hide
  const SHOW_THRESHOLD = 20; // px upward to trigger show

  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const currentY = window.scrollY;
      const delta = currentY - lastY;

      // Ignore micro-jitter (< 2px) caused by mobile address bar resize
      if (Math.abs(delta) >= 2) {
        accDelta += delta;

        siteTop.classList.toggle('site-top--scrolled', currentY > 10);

        if (accDelta > HIDE_THRESHOLD && currentY > 120) {
          siteTop.classList.add('site-top--hidden');
          accDelta = 0;
        } else if (accDelta < -SHOW_THRESHOLD) {
          siteTop.classList.remove('site-top--hidden');
          accDelta = 0;
        }

        lastY = currentY;
      }

      ticking = false;
    });
  }, { passive: true });
}

function initFooterAccordion() {
  $$('.site-footer__col-toggle').forEach(btn => {
    btn.addEventListener('click', () => {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
  });
}

// For the hero + product carousels we use CSS scroll snap.

function initScrollCarousels() {
  $$('[data-carousel]').forEach((el) => {
    const viewport = el.querySelector('[data-carousel-viewport]');
    const container = el.querySelector('[data-carousel-container]');
    const prevBtn = el.querySelector('[data-carousel-prev]');
    const nextBtn = el.querySelector('[data-carousel-next]');
    const dotsContainer = el.querySelector('[data-carousel-dots]');

    if (!viewport || !container) return;

    const type = el.dataset.carousel;
    let currentIndex = 0;
    let autoPlayTimer = null;

    function getSlides() {
      return [...container.children];
    }

    function animateContent(slide) {
      const content = slide?.querySelector('.hero-carousel__content');
      if (!content) return;
      content.classList.remove('is-visible');
      requestAnimationFrame(() => requestAnimationFrame(() => content.classList.add('is-visible')));
    }

    function scrollTo(index) {
      const slides = getSlides();
      if (!slides.length) return;
      const total = slides.length;
      currentIndex = ((index % total) + total) % total;
      const slide = slides[currentIndex];
      viewport.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      updateDots();
      slides.forEach((s) => s.classList.remove('is-active'));
      slide.classList.add('is-active');
      if (type === 'hero') animateContent(slide);
    }

    // Reassignable so hero can override with clone-based infinite loop
    let scrollNext = () => scrollTo(currentIndex + 1);
    let scrollPrev = () => scrollTo(currentIndex - 1);

    // Detect current slide on scroll (hero manages its own state via clones)
    // Cache slide positions to avoid reading offsetLeft on every scroll event.
    let cachedSlidePositions = [];
    function refreshSlidePositions() {
      const slides = getSlides();
      cachedSlidePositions = slides.map(s => s.offsetLeft);
    }
    requestAnimationFrame(refreshSlidePositions);
    window.addEventListener('resize', () => {
      requestAnimationFrame(refreshSlidePositions);
    }, { passive: true });

    let scrollRaf;
    on(viewport, 'scroll', () => {
      if (type === 'hero') return;
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(() => {
        if (!cachedSlidePositions.length) return;
        const scrollLeft = viewport.scrollLeft;
        let closest = 0;
        let minDiff = Infinity;
        cachedSlidePositions.forEach((pos, i) => {
          const diff = Math.abs(pos - scrollLeft);
          if (diff < minDiff) { minDiff = diff; closest = i; }
        });
        if (closest !== currentIndex) {
          currentIndex = closest;
          updateDots();
        }
      });
    });

    function updateDots() {
      if (!dotsContainer) return;
      $$('[data-dot]', dotsContainer).forEach((dot, i) => {
        dot.querySelector('.hero-dot')?.classList.toggle('is-active', i === currentIndex);
      });
    }

    on(prevBtn, 'click', scrollPrev);
    on(nextBtn, 'click', scrollNext);

    // Dot clicks (hero uses heroGoTo if defined, others use scrollTo)
    let heroGoTo = null;
    if (dotsContainer) {
      $$('[data-dot]', dotsContainer).forEach((dot, i) => {
        on(dot, 'click', () => heroGoTo ? heroGoTo(i) : scrollTo(i));
      });
    }

    // Animate first slide content on load
    if (type === 'hero') {
      el.classList.add('hero-carousel--js');
      const firstSlide = getSlides()[0];
      setTimeout(() => animateContent(firstSlide), 200);
    }

    // Auto-play for hero — restarts after user interaction
    if (type === 'hero') {
      let resumeTimer = null;

      function startAutoPlay() {
        clearInterval(autoPlayTimer);
        autoPlayTimer = setInterval(() => scrollNext(), 5000);
      }

      startAutoPlay();

      on(viewport, 'pointerdown', () => {
        clearInterval(autoPlayTimer);
        clearTimeout(resumeTimer);
        resumeTimer = setTimeout(startAutoPlay, 8000);
      });
    }

    // Hero carousel: clone-based infinite loop + one-slide-at-a-time touch
    if (type === 'hero') {
      const realSlides = [...container.children]; // captured before cloning
      const total = realSlides.length;

      if (total >= 2) {
        // Prepend clone of last, append clone of first
        container.prepend(realSlides[total - 1].cloneNode(true));
        container.appendChild(realSlides[0].cloneNode(true));

        // Cache slide width to avoid forced reflow on every navigation.
        // Hero slides are always 100vw, so position = index * slideW.
        // Layout: [clone-last][real0][real1]...[realN-1][clone-first]
        // realSlides[i].offsetLeft === (i + 1) * slideW
        let slideW = viewport.offsetWidth;
        window.addEventListener('resize', () => {
          requestAnimationFrame(() => { slideW = viewport.offsetWidth; });
        }, { passive: true });

        // Position at first real slide after layout settles
        requestAnimationFrame(() => {
          viewport.scrollTo({ left: slideW, behavior: 'instant' });
        });

        let isTransitioning = false;
        const ANIM_MS = 380;

        // heroGoTo: uses cached slideW instead of reading offsetLeft (no forced reflow)
        heroGoTo = (index, behavior = 'smooth') => {
          currentIndex = ((index % total) + total) % total;
          viewport.scrollTo({ left: (currentIndex + 1) * slideW, behavior });
          realSlides.forEach(s => s.classList.remove('is-active'));
          realSlides[currentIndex].classList.add('is-active');
          updateDots();
          animateContent(realSlides[currentIndex]);
        };

        scrollNext = () => {
          if (isTransitioning) return;
          if (currentIndex === total - 1) {
            // Scroll to clone-first at the end (smooth), then silently jump to real first
            isTransitioning = true;
            viewport.scrollTo({ left: (total + 1) * slideW, behavior: 'smooth' });
            setTimeout(() => {
              heroGoTo(0, 'instant');
              isTransitioning = false;
            }, ANIM_MS);
          } else {
            heroGoTo(currentIndex + 1);
          }
        };

        scrollPrev = () => {
          if (isTransitioning) return;
          if (currentIndex === 0) {
            // Scroll to clone-last at the start (smooth), then silently jump to real last
            isTransitioning = true;
            viewport.scrollTo({ left: 0, behavior: 'smooth' });
            setTimeout(() => {
              heroGoTo(total - 1, 'instant');
              isTransitioning = false;
            }, ANIM_MS);
          } else {
            heroGoTo(currentIndex - 1);
          }
        };
      }

      // Intercept touch: one slide at a time, no native momentum
      let touchStartX = 0;
      let touchMoved = false;

      viewport.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchMoved = false;
      }, { passive: true });

      viewport.addEventListener('touchmove', (e) => {
        if (Math.abs(e.touches[0].clientX - touchStartX) > 5) {
          touchMoved = true;
          e.preventDefault();
        }
      }, { passive: false });

      viewport.addEventListener('touchend', (e) => {
        if (!touchMoved) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 30) {
          dx < 0 ? scrollNext() : scrollPrev();
        }
        touchMoved = false;
      }, { passive: true });
    }

    // Product carousel drag-free + infinite loop
    if (type === 'products') {
      let isDragging = false;
      let startX = 0;
      let scrollStart = 0;
      let resumeTimer = null;
      let silentJump = false;

      // Infinite loop: clone slides before and after
      if (el.dataset.infinite !== undefined) {
        const origSlides = [...container.children];
        const n = origSlides.length;
        if (n >= 2) {
          origSlides.forEach(s => container.appendChild(s.cloneNode(true)));
          [...origSlides].reverse().forEach(s => container.prepend(s.cloneNode(true)));

          requestAnimationFrame(() => {
            const allSlides = [...container.children];
            const firstOrig = allSlides[n];

            // Cache positions once — avoids forced reflow on every scroll event.
            // origStart/origEnd were previously functions calling offsetLeft each time.
            let origStartPos = firstOrig.offsetLeft;
            let setWidth     = allSlides[n * 2].offsetLeft - origStartPos;
            let origEndPos   = origStartPos + setWidth;

            // Update cache on resize so positions stay accurate
            window.addEventListener('resize', () => {
              requestAnimationFrame(() => {
                origStartPos = allSlides[n].offsetLeft;
                setWidth     = allSlides[n * 2].offsetLeft - origStartPos;
                origEndPos   = origStartPos + setWidth;
              });
            }, { passive: true });

            viewport.scrollLeft = origStartPos;

            on(viewport, 'scroll', () => {
              if (silentJump) return;
              const sl = viewport.scrollLeft;
              if (sl < origStartPos - 8) {
                silentJump = true;
                viewport.style.scrollSnapType = 'none';
                viewport.scrollLeft += setWidth;
                requestAnimationFrame(() => { viewport.style.scrollSnapType = ''; silentJump = false; });
              } else if (sl >= origEndPos - 8) {
                silentJump = true;
                viewport.style.scrollSnapType = 'none';
                viewport.scrollLeft -= setWidth;
                requestAnimationFrame(() => { viewport.style.scrollSnapType = ''; silentJump = false; });
              }
            });
          });
        }
      }

      let hasDragged = false;
      let isMouseDown = false;
      viewport.style.cursor = 'grab';

      on(viewport, 'pointerdown', (e) => {
        if (e.pointerType === 'touch') return; // touch: browser handles via touch-action
        if (e.button !== 0) return;
        isDragging = false;
        hasDragged = false;
        isMouseDown = true;
        startX = e.clientX;
        scrollStart = viewport.scrollLeft;
        // Do NOT setPointerCapture here — capturing early causes the click event to
        // fire on the viewport instead of the <a> product card, blocking navigation.
        // Capture is set only when a real drag is confirmed (>20 px movement).
        viewport.style.cursor = 'grabbing';
        viewport.style.userSelect = 'none';
        clearInterval(autoPlayTimer);
        clearTimeout(resumeTimer);
      });
      on(viewport, 'pointermove', (e) => {
        if (!isMouseDown && !viewport.hasPointerCapture(e.pointerId)) return;
        const dx = e.clientX - startX;
        if (!isDragging && Math.abs(dx) > 5) {
          isDragging = true;
          hasDragged = true;
          // Capture now so we keep tracking even if the pointer leaves the viewport
          viewport.setPointerCapture(e.pointerId);
        }
        if (isDragging) {
          e.preventDefault();
          viewport.scrollLeft = scrollStart - dx;
        }
      });
      on(viewport, 'pointerup', (e) => {
        if (!isMouseDown && !viewport.hasPointerCapture(e.pointerId)) return;
        isMouseDown = false;
        if (viewport.hasPointerCapture(e.pointerId)) viewport.releasePointerCapture(e.pointerId);
        viewport.style.cursor = 'grab';
        viewport.style.userSelect = '';
        isDragging = false;
        // Safety net: reset hasDragged even if the post-drag click never fires
        // (e.g. user drags and releases outside the viewport)
        setTimeout(() => { hasDragged = false; }, 50);
        if (el.dataset.autoplay !== undefined) {
          resumeTimer = setTimeout(startProductAutoPlay, 3000);
        }
      });
      on(viewport, 'pointercancel', (e) => {
        if (!isMouseDown && !viewport.hasPointerCapture(e.pointerId)) return;
        isMouseDown = false;
        if (viewport.hasPointerCapture(e.pointerId)) viewport.releasePointerCapture(e.pointerId);
        viewport.style.cursor = 'grab';
        viewport.style.userSelect = '';
        isDragging = false;
      });
      // Prevent accidental navigation/click after a mouse drag gesture
      viewport.addEventListener('click', (e) => {
        if (hasDragged) {
          e.preventDefault();
          hasDragged = false;
        }
      }, true);

      function startProductAutoPlay() {
        clearInterval(autoPlayTimer);
        const interval = parseInt(el.dataset.autoplayInterval) || 4000;
        autoPlayTimer = setInterval(scrollNext, interval);
      }

      if (el.dataset.autoplay !== undefined) {
        startProductAutoPlay();
        on(el, 'mouseenter', () => clearInterval(autoPlayTimer));
        on(el, 'mouseleave', () => startProductAutoPlay());
      }
    }
  });
}

/* ============================================================
   CART — Shopify Ajax API
   ============================================================ */
const Cart = (() => {
  let state = { items: [], token: null };

  async function fetchCart() {
    const res = await fetch('/cart.js');
    const data = await res.json();
    state = data;
    return data;
  }

  async function addItem({ variantId, quantity = 1 }) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!res.ok) throw new Error('Could not add to cart');
    return res.json();
  }

  async function updateItem(variantId, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    return res.json();
  }

  async function removeItem(variantId) {
    return updateItem(variantId, 0);
  }

  function getCheckoutUrl() {
    return '/checkout';
  }

  return { fetchCart, addItem, updateItem, removeItem, getCheckoutUrl, getState: () => state };
})();

/* ============================================================
   CART DRAWER
   ============================================================ */
function initCartDrawer() {
  const overlay = $('#cart-overlay');
  if (!overlay) return;

  function openDrawer() {
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    renderCart(true); // true = fire view_cart event
  }

  function closeDrawer() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // Expose globally — triggers attached via document delegation in boot
  window.LuckyCart = { open: openDrawer, close: closeDrawer, refresh: renderCart };

  async function renderCart(fireViewCart = false) {
    const cartBody = $('#cart-body');
    const cartFooter = $('#cart-footer');
    if (!cartBody) return;

    try {
      const cart = await Cart.fetchCart();
      updateBadge(cart.item_count);
      if (fireViewCart && cart.item_count > 0) LuckyTracking.viewCart(cart);

      if (cart.item_count === 0) {
        cartBody.innerHTML = `
          <div class="cart-drawer__empty">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z M3 6h18 M16 10a4 4 0 01-8 0"/>
            </svg>
            <p>Sua sacola está vazia</p>
            <button data-cart-close>Continuar comprando</button>
          </div>`;
        cartBody.querySelectorAll('[data-cart-close]').forEach((b) => on(b, 'click', closeDrawer));
        if (cartFooter) cartFooter.style.display = 'none';
        const shippingBar = $('#cart-shipping-bar');
        if (shippingBar) shippingBar.style.display = 'none';
        return;
      }

      if (cartFooter) cartFooter.style.display = '';

      cartBody.innerHTML = cart.items.map((item) => `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__image">
            ${item.image ? `<img src="${item.image}" alt="${item.product_title}" loading="lazy">` : ''}
          </div>
          <div class="cart-item__info">
            <div class="cart-item__title">${item.product_title}</div>
            ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-item__variant">${item.variant_title}</div>` : ''}
            ${item.properties && item.properties['Adicionais'] ? `<div class="cart-item__variant">${item.properties['Adicionais']}</div>` : ''}
            <div class="cart-item__price">${formatMoney(item.price / 100)}</div>
            <div class="cart-item__controls">
              <button class="qty-btn" data-qty-minus data-key="${item.key}" data-qty="${item.quantity}" aria-label="Diminuir">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4"/></svg>
              </button>
              <span class="qty-value">${item.quantity}</span>
              <button class="qty-btn" data-qty-plus data-key="${item.key}" data-qty="${item.quantity}" aria-label="Aumentar">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
              </button>
              <button class="cart-item__remove" data-remove data-key="${item.key}" aria-label="Remover">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>
              </button>
            </div>
          </div>
        </div>`).join('');

      // Update total
      const totalEl = $('#cart-total');
      if (totalEl) totalEl.textContent = formatMoney(cart.total_price / 100);
      updateShippingBar(cart.total_price);

      // Events — use item.key (unique per line) to avoid affecting duplicate variant_ids
      cartBody.querySelectorAll('[data-qty-minus]').forEach((btn) => {
        on(btn, 'click', async () => {
          const key = btn.dataset.key;
          const qty = parseInt(btn.dataset.qty) || 1;
          await Cart.updateItem(key, Math.max(0, qty - 1));
          renderCart();
        });
      });
      cartBody.querySelectorAll('[data-qty-plus]').forEach((btn) => {
        on(btn, 'click', async () => {
          const key = btn.dataset.key;
          const qty = parseInt(btn.dataset.qty) || 1;
          await Cart.updateItem(key, qty + 1);
          renderCart();
        });
      });
      cartBody.querySelectorAll('[data-remove]').forEach((btn) => {
        on(btn, 'click', async () => {
          // Capture item data before removing for tracking
          const removedItem = (cart.items || []).find((i) => i.key === btn.dataset.key);
          await Cart.removeItem(btn.dataset.key);
          if (removedItem) LuckyTracking.removeFromCart(removedItem);
          renderCart();
        });
      });

    } catch (e) {
      console.error('Cart error:', e);
    }
  }

  // Free shipping progress bar
  function updateShippingBar(totalCents) {
    const bar = $('#cart-shipping-bar');
    if (!bar) return;
    const threshold = parseInt(bar.dataset.threshold || '0', 10);
    if (!threshold) return;
    bar.style.display = '';
    const fillEl = $('#cart-shipping-fill');
    const textEl = $('#cart-shipping-text');
    if (totalCents >= threshold) {
      bar.classList.add('cart-shipping-bar--unlocked');
      if (textEl) textEl.innerHTML = '<strong>✓ Frete grátis desbloqueado!</strong>';
      if (fillEl) fillEl.style.width = '100%';
    } else {
      bar.classList.remove('cart-shipping-bar--unlocked');
      const remaining = threshold - totalCents;
      if (textEl) textEl.innerHTML = `Falta <strong>${formatMoney(remaining / 100)}</strong> para frete grátis`;
      if (fillEl) fillEl.style.width = `${Math.min(98, (totalCents / threshold) * 100)}%`;
    }
  }

  // Checkout button — calls yampiClick() (domain fix applied in YampiSnippet)
  on($('#cart-checkout-btn'), 'click', () => {
    const doCheckout = () => {
      if (typeof yampiClick === 'function') {
        yampiClick();
      } else {
        window.location.href = '/cart';
      }
    };
    Cart.fetchCart().then((cart) => {
      LuckyTracking.beginCheckout(cart);
    }).finally(() => { doCheckout(); });
  });

  // Initial badge on page load
  Cart.fetchCart().then((cart) => updateBadge(cart.item_count));
}

function updateBadge(count) {
  $$('[data-cart-count]').forEach((el) => {
    if (count > 0) {
      el.textContent = count;
      el.style.display = 'flex';
    } else {
      el.style.display = 'none';
    }
  });
  $$('[data-cart-count-inline]').forEach((el) => {
    el.textContent = count > 0 ? `(${count})` : '';
  });
  document.dispatchEvent(new CustomEvent('lucky:cart-updated', { detail: { count } }));
}

/* ============================================================
   ADD TO CART — product cards & product page
   ============================================================ */
async function addToCartHandler(variantId, btn) {
  if (!variantId) return;
  const originalText = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = `<svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="14" height="14"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>`;

  try {
    const item = await Cart.addItem({ variantId });
    showToast('Adicionado à sacola! 🛍️');
    if (window.LuckyCart) window.LuckyCart.open();

    if (item) LuckyTracking.addToCart(item);
  } catch (e) {
    showToast('Erro ao adicionar. Tente novamente.', 'error');
  } finally {
    btn.disabled = false;
    btn.innerHTML = originalText;
  }
}

function initAddToCartButtons() {
  $$('[data-add-to-cart]').forEach((btn) => {
    on(btn, 'click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const variantId = btn.dataset.variantId || btn.closest('[data-variant-id]')?.dataset.variantId;
      await addToCartHandler(variantId, btn);
    });
  });
}

/* ============================================================
   GALLERY LIGHTBOX — reusable for any page with data-gallery
   ============================================================ */
function initGalleryLightbox(containerEl, activeThumbSelector) {
  if (!containerEl) return;
  let images = [];
  try { images = JSON.parse(containerEl.dataset.gallery || '[]'); } catch(e) {}
  if (!images.length) return;

  let current = 0;

  const lb = document.createElement('div');
  lb.className = 'product-lightbox';
  lb.setAttribute('role', 'dialog');
  lb.setAttribute('aria-modal', 'true');
  lb.setAttribute('aria-label', 'Visualizar imagens');
  lb.innerHTML = `
    <button class="product-lightbox__close" aria-label="Fechar">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="28" height="28">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>
    <div class="product-lightbox__main">
      <button class="product-lightbox__btn product-lightbox__btn--prev" aria-label="Anterior">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <img class="product-lightbox__img" src="" alt="" loading="eager">
      <button class="product-lightbox__btn product-lightbox__btn--next" aria-label="Próxima">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
      <span class="product-lightbox__counter"></span>
    </div>
    <div class="product-lightbox__thumbs"></div>
  `;
  document.body.appendChild(lb);

  const lbImg     = lb.querySelector('.product-lightbox__img');
  const lbThumbs  = lb.querySelector('.product-lightbox__thumbs');
  const lbCounter = lb.querySelector('.product-lightbox__counter');
  const btnPrev   = lb.querySelector('.product-lightbox__btn--prev');
  const btnNext   = lb.querySelector('.product-lightbox__btn--next');
  const btnClose  = lb.querySelector('.product-lightbox__close');

  images.forEach((img, i) => {
    const t = document.createElement('button');
    t.className = 'product-lightbox__thumb';
    t.innerHTML = `<img src="${img.src.replace(/width=\d+/, 'width=150')}" alt="${img.alt}" loading="lazy">`;
    t.addEventListener('click', () => goTo(i));
    lbThumbs.appendChild(t);
  });

  function updateCounter() { lbCounter.textContent = `${current + 1} / ${images.length}`; }

  function goTo(idx) {
    current = (idx + images.length) % images.length;
    lbImg.style.transform = '';
    lbImg.style.transformOrigin = 'center center';
    lbImg.classList.add('is-loading');
    const newImg = new Image();
    newImg.onload = () => { lbImg.src = images[current].src; lbImg.alt = images[current].alt; lbImg.classList.remove('is-loading'); };
    newImg.src = images[current].src;
    lbThumbs.querySelectorAll('.product-lightbox__thumb').forEach((t, i) => t.classList.toggle('is-active', i === current));
    updateCounter();
    const at = lbThumbs.children[current];
    if (at) at.scrollIntoView({ inline: 'center', behavior: 'smooth' });
  }

  function openLightbox(startIdx) { document.body.style.overflow = 'hidden'; lb.classList.add('is-open'); goTo(startIdx); }
  function closeLightbox() { lb.classList.remove('is-open'); document.body.style.overflow = ''; }

  // Thumbnails dentro do container abrem o lightbox no índice correto
  containerEl.querySelectorAll('[data-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => openLightbox(parseInt(thumb.dataset.thumb) || 0));
  });
  containerEl.querySelectorAll('.pers-thumb').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const src = thumb.dataset.src || '';
      const idx = images.findIndex((img) => img.src === src || src.includes(img.src.split('?')[0].split('/').pop().split('_')[0]));
      openLightbox(idx >= 0 ? idx : 0);
    });
  });

  const mainWrap = containerEl.querySelector('[data-open-lightbox]');
  if (mainWrap) {
    mainWrap.addEventListener('click', () => {
      let idx = 0;
      if (activeThumbSelector) {
        const base = activeThumbSelector.replace('.is-active', '');
        const all = containerEl.querySelectorAll(base);
        const active = containerEl.querySelector(activeThumbSelector);
        if (active) idx = Math.max([...all].indexOf(active), 0);
      }
      openLightbox(idx);
    });
    mainWrap.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); mainWrap.click(); } });
  }

  btnClose.addEventListener('click', closeLightbox);
  btnPrev.addEventListener('click', () => goTo(current - 1));
  btnNext.addEventListener('click', () => goTo(current + 1));

  document.addEventListener('keydown', (e) => {
    if (!lb.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') goTo(current - 1);
    if (e.key === 'ArrowRight') goTo(current + 1);
  });

  lb.addEventListener('click', (e) => {
    if (e.target === lb || e.target === lb.querySelector('.product-lightbox__main')) closeLightbox();
  });

  // Touch: swipe + pinch zoom
  let touchStartX = 0, pinchStartDist = 0, currentScale = 1, isPinching = false;
  function getTouchDist(t) { const dx = t[0].clientX - t[1].clientX, dy = t[0].clientY - t[1].clientY; return Math.sqrt(dx*dx + dy*dy); }
  lb.addEventListener('touchstart', (e) => {
    if (e.touches.length === 2) { isPinching = true; pinchStartDist = getTouchDist(e.touches); }
    else { isPinching = false; touchStartX = e.touches[0].clientX; }
  }, { passive: true });
  lb.addEventListener('touchmove', (e) => {
    if (e.touches.length === 2 && isPinching) {
      const scale = Math.min(Math.max(currentScale * getTouchDist(e.touches) / pinchStartDist, 1), 4);
      lbImg.style.transform = `scale(${scale})`;
    }
  }, { passive: true });
  lb.addEventListener('touchend', (e) => {
    if (isPinching) {
      const m = lbImg.style.transform.match(/scale\(([^)]+)\)/);
      currentScale = m ? Math.min(Math.max(parseFloat(m[1]), 1), 4) : 1;
      if (e.touches.length === 0) isPinching = false;
    } else if (currentScale === 1) {
      const dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) > 50) goTo(dx < 0 ? current + 1 : current - 1);
    }
  });

  if (images.length <= 1) { btnPrev.style.display = 'none'; btnNext.style.display = 'none'; lbThumbs.style.display = 'none'; }
}

/* ============================================================
   PRODUCT PAGE — variant selector + gallery + qty
   ============================================================ */
function initProductPage() {
  const page = $('#product-page');
  if (!page) return;

  // Copy link button
  const copyBtn = $('[data-copy-link]', page);
  if (copyBtn) {
    on(copyBtn, 'click', () => {
      navigator.clipboard.writeText(window.location.href).then(() => {
        copyBtn.classList.add('copied');
        const copyIcon = copyBtn.querySelector('.copy-icon');
        const checkIcon = copyBtn.querySelector('.check-icon');
        if (copyIcon) copyIcon.style.display = 'none';
        if (checkIcon) checkIcon.style.display = '';
        setTimeout(() => {
          copyBtn.classList.remove('copied');
          if (copyIcon) copyIcon.style.display = '';
          if (checkIcon) checkIcon.style.display = 'none';
        }, 2000);
      });
    });
  }

  // Gallery thumbnails → swap main image
  $$('[data-thumb]', page).forEach((thumb) => {
    on(thumb, 'click', () => {
      const mainImg = $('#product-main-img');
      if (mainImg) {
        mainImg.src = thumb.dataset.src;
        mainImg.srcset = '';
      }
      $$('[data-thumb]', page).forEach((t) => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
    });
  });

  // Lightbox
  initGalleryLightbox(page, '[data-thumb].is-active');

  // Product tabs
  $$('[data-tab]', page.parentElement || document).forEach((btn) => {
    on(btn, 'click', () => {
      const tabId = btn.dataset.tab;
      $$('[data-tab]', btn.closest('.product-tabs__nav')).forEach((b) => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');
      $$('[data-panel]', btn.closest('.product-tabs')).forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.panel === tabId);
      });
    });
  });

  // Variant selection
  let selectedVariantId = page.dataset.defaultVariant;
  let selectedVariantPrice = parseFloat(page.dataset.defaultPrice || '0');
  const variantBtnsEl = $$('[data-variant-btn]', page);
  // Only require explicit selection when there are multiple variant options shown
  let variantExplicitlySelected = variantBtnsEl.length === 0;

  variantBtnsEl.forEach((btn) => {
    on(btn, 'click', () => {
      variantBtnsEl.forEach((b) => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      variantExplicitlySelected = true;
      selectedVariantId = btn.dataset.variantId;
      selectedVariantPrice = parseFloat(btn.dataset.price || '0');
      document.dispatchEvent(new CustomEvent('lucky:variant-change', { detail: { variantId: selectedVariantId } }));

      // Update price display (selectedVariantPrice is in cents)
      const priceEl = $('#product-price');
      if (priceEl) priceEl.textContent = formatMoney(selectedVariantPrice / 100);
      const installEl = $('#product-installments');
      if (installEl) installEl.textContent = getInstallments(String(selectedVariantPrice / 100));

      // Update availability
      const addBtn = $('#product-add-btn');
      const available = btn.dataset.available !== 'false';
      if (addBtn) {
        addBtn.disabled = !available;
        addBtn.textContent = available ? 'Adicionar à Sacola' : 'Esgotado';
      }
      // Sync sticky CTA
      const stickyPrice = $('#sticky-atc-price');
      if (stickyPrice) stickyPrice.textContent = formatMoney(selectedVariantPrice / 100);
      const stickyBtn = $('#sticky-atc-btn');
      if (stickyBtn) {
        stickyBtn.disabled = !available;
        stickyBtn.textContent = available ? 'Adicionar à Sacola' : 'Esgotado';
      }
    });
  });

  // Quantity
  let qty = 1;
  const qtyDisplay = $('#product-qty');
  on($('#product-qty-minus'), 'click', () => {
    if (qty > 1) { qty--; if (qtyDisplay) qtyDisplay.textContent = qty; }
  });
  on($('#product-qty-plus'), 'click', () => {
    qty++;
    if (qtyDisplay) qtyDisplay.textContent = qty;
  });

  // Sticky CTA button delegates to main button
  on($('#sticky-atc-btn'), 'click', () => $('#product-add-btn')?.click());

  // Add to cart
  const addBtn = $('#product-add-btn');
  on(addBtn, 'click', async () => {
    if (!selectedVariantId) return;
    if (!variantExplicitlySelected) {
      showToast('Por favor, selecione uma variação antes de continuar.', 'error');
      return;
    }
    addBtn.disabled = true;
    const original = addBtn.innerHTML;
    addBtn.innerHTML = '<svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="16" height="16"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>';
    try {
      const item = await Cart.addItem({ variantId: selectedVariantId, quantity: qty });
      showToast('Adicionado à sacola! 🛍️');
      if (item) LuckyTracking.addToCart(item);
      document.dispatchEvent(new CustomEvent('lucky:variant-change', { detail: { variantId: selectedVariantId } }));
      if (window.LuckyCart) window.LuckyCart.open();
    } catch {
      showToast('Erro ao adicionar. Tente novamente.', 'error');
    } finally {
      addBtn.disabled = false;
      addBtn.innerHTML = original;
    }
  });
}

/* ============================================================
   COLLECTION PAGE — filters + sort
   ============================================================ */
function initCollectionPage() {
  const page = $('#collection-page');
  if (!page) return;

  // On paginated pages, scroll to the product grid instead of staying at the top
  if (new URLSearchParams(window.location.search).has('page')) {
    const grid = $('#product-grid');
    const header = document.querySelector('.site-top');
    if (grid) {
      const headerH = header ? header.offsetHeight : 0;
      window.scrollTo({ top: grid.offsetTop - headerH - 12, behavior: 'instant' });
    }
  }

  const filterPanel = $('#filter-panel');
  const filterToggle = $('#filter-toggle');
  const filterClose = $('#filter-close');
  const sortSelect = $('#sort-select');
  const filterReset = $('#filter-reset');
  const onSaleToggle = $('#on-sale-toggle');
  const productGrid = $('#product-grid');
  const filterBadge = $('#filter-badge');

  // Filter panel toggle
  on(filterToggle, 'click', () => filterPanel?.classList.toggle('is-open'));
  on(filterClose, 'click', () => filterPanel?.classList.remove('is-open'));

  // Category filter tags
  $$('[data-filter-tag]', page).forEach((tag) => {
    on(tag, 'click', () => {
      tag.classList.toggle('is-active');
      applyFilters();
    });
  });

  // Price range
  const priceMin = $('#price-min');
  const priceMax = $('#price-max');
  const priceMinVal = $('#price-min-val');
  const priceMaxVal = $('#price-max-val');
  on(priceMin, 'input', () => {
    if (priceMinVal) priceMinVal.textContent = formatMoney(parseFloat(priceMin.value));
    applyFilters();
  });
  on(priceMax, 'input', () => {
    if (priceMaxVal) priceMaxVal.textContent = formatMoney(parseFloat(priceMax.value));
    applyFilters();
  });

  // On sale toggle
  on(onSaleToggle, 'change', applyFilters);

  // Sort
  on(sortSelect, 'change', applyFilters);

  // Reset
  on(filterReset, 'click', () => {
    $$('[data-filter-tag].is-active', page).forEach((t) => t.classList.remove('is-active'));
    if (priceMin) priceMin.value = priceMin.min;
    if (priceMax) priceMax.value = priceMax.max;
    if (priceMinVal) priceMinVal.textContent = formatMoney(parseFloat(priceMin?.min || '0'));
    if (priceMaxVal) priceMaxVal.textContent = formatMoney(parseFloat(priceMax?.max || '9999'));
    if (onSaleToggle) onSaleToggle.checked = false;
    if (sortSelect) sortSelect.value = 'az';
    applyFilters();
  });

  function applyFilters() {
    if (!productGrid) return;

    const cards = $$('[data-product-card]', productGrid);
    const activeCategories = $$('[data-filter-tag].is-active', page).map((t) => t.dataset.filterTag);
    const minPrice = parseFloat(priceMin?.value || '0');
    const maxPrice = parseFloat(priceMax?.value || '999999');
    const onlyOnSale = onSaleToggle?.checked || false;
    const sortVal = sortSelect?.value || 'az';

    let visible = cards.filter((card) => {
      const price = parseFloat(card.dataset.price || '0');
      const category = card.dataset.category || '';
      const hasDiscount = card.dataset.hasDiscount === 'true';

      if (activeCategories.length && !activeCategories.includes(category)) return false;
      if (price < minPrice || price > maxPrice) return false;
      if (onlyOnSale && !hasDiscount) return false;
      return true;
    });

    // Sort
    visible.sort((a, b) => {
      const titleA = a.dataset.title || '';
      const titleB = b.dataset.title || '';
      const priceA = parseFloat(a.dataset.price || '0');
      const priceB = parseFloat(b.dataset.price || '0');
      const dateA = parseInt(a.dataset.createdAt || '0', 10);
      const dateB = parseInt(b.dataset.createdAt || '0', 10);
      switch (sortVal) {
        case 'za': return titleB.localeCompare(titleA);
        case 'price-asc': return priceA - priceB;
        case 'price-desc': return priceB - priceA;
        case 'newest': return dateB - dateA;
        default: return titleA.localeCompare(titleB);
      }
    });

    // Show/hide
    const visibleSet = new Set(visible);
    cards.forEach((card) => {
      card.style.display = visibleSet.has(card) ? '' : 'none';
    });

    // Reorder DOM
    visible.forEach((card) => productGrid.appendChild(card));

    // Count — when no filters are active, show the real total from the collection
    const countEl = $('#product-count');
    if (countEl) {
      const hasActiveFilters =
        activeCategories.length > 0 ||
        onlyOnSale ||
        parseFloat(priceMin?.value ?? priceMin?.min ?? '0') > parseFloat(priceMin?.min ?? '0') ||
        parseFloat(priceMax?.value ?? priceMax?.max ?? '999999') < parseFloat(priceMax?.max ?? '999999');
      const displayCount = hasActiveFilters
        ? visible.length
        : parseInt(countEl.dataset.total ?? visible.length, 10);
      countEl.textContent = `${displayCount} ${displayCount === 1 ? 'produto' : 'produtos'}`;
    }

    // Badge
    const activeCount = activeCategories.length + (onlyOnSale ? 1 : 0) +
      ((parseFloat(priceMin?.value || priceMin?.min) > parseFloat(priceMin?.min || '0') ||
        parseFloat(priceMax?.value || priceMax?.max) < parseFloat(priceMax?.max || '9999')) ? 1 : 0);

    if (filterBadge) {
      filterBadge.textContent = activeCount;
      filterBadge.style.display = activeCount > 0 ? 'flex' : 'none';
    }
    if (filterReset) {
      filterReset.style.display = activeCount > 0 ? 'inline' : 'none';
    }
  }

}

/* ============================================================
   SEARCH OVERLAY
   ============================================================ */
function initSearch() {
  const overlay = $('#search-overlay');
  if (!overlay) return;

  const input = $('#search-input');
  const clearBtn = $('#search-clear');
  const closeBtn = $('#search-close');
  const resultsEl = $('#search-results');
  const defaultEl = $('#search-default');
  const resultsCountEl = $('#search-results-count');

  const openBtns = $$('[data-search-open]');

  let debounceTimer;
  let recentSearches = [];

  function openSearch() {
    if (resultsEl) resultsEl.style.display = 'none';
    if (defaultEl) defaultEl.style.display = 'flex';
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    loadRecent();
    renderTrending();
    if (!window.matchMedia('(max-width: 767px)').matches) {
      setTimeout(() => input?.focus(), 100);
    }
  }

  function closeSearch() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    if (input) input.value = '';
    if (clearBtn) clearBtn.style.display = 'none';
    if (resultsEl) resultsEl.style.display = 'none';
    if (defaultEl) defaultEl.style.display = 'flex';
  }

  on(closeBtn, 'click', closeSearch);

  on(input, 'input', (e) => {
    const q = e.target.value.trim();
    clearTimeout(debounceTimer);
    if (clearBtn) clearBtn.style.display = q ? '' : 'none';

    if (!q) {
      if (resultsEl) resultsEl.style.display = 'none';
      if (defaultEl) defaultEl.style.display = 'flex';
      return;
    }

    debounceTimer = setTimeout(() => searchProducts(q), 400);
  });

  on(clearBtn, 'click', () => {
    if (input) input.value = '';
    if (clearBtn) clearBtn.style.display = 'none';
    if (resultsEl) resultsEl.style.display = 'none';
    if (defaultEl) defaultEl.style.display = 'flex';
    input?.focus();
  });

  // Backdrop click (mobile) closes search
  on($('.search-overlay__backdrop'), 'click', closeSearch);

  // Track enter key to save frequency
  on(input, 'keydown', (e) => {
    if (e.key === 'Enter') {
      const q = input?.value.trim();
      if (q) { saveSearch(q); trackFreq(q); }
    }
  });

  const hideOutOfStock = overlay.dataset.hideOutOfStock === 'true';

  async function searchProducts(query) {
    if (!resultsEl) return;
    resultsEl.style.display = '';
    if (defaultEl) defaultEl.style.display = 'none';
    if (resultsCountEl) resultsCountEl.textContent = 'Buscando...';

    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=24`);
      const data = await res.json();
      let products = data.resources?.results?.products || [];
      if (hideOutOfStock) products = products.filter((p) => p.available);
      products = products.slice(0, 12);

      if (resultsCountEl) resultsCountEl.textContent = `${products.length} resultado${products.length !== 1 ? 's' : ''}`;

      const gridEl = $('#search-results-grid');
      if (!gridEl) return;

      if (!products.length) {
        gridEl.innerHTML = `<div style="grid-column:1/-1;text-align:center;padding:3rem 0">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style="width:40px;height:40px;color:var(--muted-foreground);opacity:0.4;margin:0 auto 0.75rem;display:block">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          <p class="font-body" style="font-size:14px;color:var(--muted-foreground)">Nenhum produto encontrado para "${query}"</p>
        </div>`;
        return;
      }

      gridEl.innerHTML = products.map((p) => `
        <a href="${p.url}" class="search-product-card" onclick="window.LuckySearch?.close()">
          <div class="search-product-card__img">
            ${p.featured_image?.url ? `<img src="${p.featured_image.url}&width=300" alt="${p.title}" loading="lazy">` : ''}
          </div>
          <div class="search-product-card__title">${p.title}</div>
          <div class="search-product-card__price">${formatMoney(parseFloat(p.price))}</div>
        </a>`).join('');

    } catch (e) {
      console.error('Search error:', e);
    }
  }

  function loadRecent() {
    try {
      recentSearches = JSON.parse(localStorage.getItem('lucky-recent-searches') || '[]');
    } catch { recentSearches = []; }
    renderRecent();
  }

  function saveSearch(term) {
    recentSearches = [term, ...recentSearches.filter((s) => s !== term)].slice(0, 5);
    localStorage.setItem('lucky-recent-searches', JSON.stringify(recentSearches));
    renderRecent();
  }

  function renderRecent() {
    const recentEl = $('#recent-searches');
    const recentContainer = $('#recent-container');
    if (!recentEl || !recentContainer) return;
    if (!recentSearches.length) {
      recentContainer.style.display = 'none';
      return;
    }
    recentContainer.style.display = '';
    recentEl.innerHTML = recentSearches.map((term) =>
      `<button class="search-tag" data-search-term="${term}">${term}</button>`
    ).join('');
    $$('[data-search-term]', recentEl).forEach((tag) => {
      on(tag, 'click', () => {
        if (input) input.value = tag.dataset.searchTerm;
        searchProducts(tag.dataset.searchTerm);
      });
    });
  }

  on($('#clear-recent'), 'click', () => {
    recentSearches = [];
    localStorage.removeItem('lucky-recent-searches');
    renderRecent();
  });

  const TRENDING_FALLBACK = ['Handchain','Charms','Argolinha','Choker','Pulseira','Anel','Brinco de Pressão','Escapulário'];

  function trackFreq(term) {
    if (!term) return;
    try {
      const freq = JSON.parse(localStorage.getItem('lucky-search-freq') || '{}');
      freq[term] = (freq[term] || 0) + 1;
      const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 30);
      localStorage.setItem('lucky-search-freq', JSON.stringify(Object.fromEntries(sorted)));
    } catch {}
  }

  function renderTrending() {
    const container = document.getElementById('trending-tags');
    if (!container) return;
    try {
      const freq = JSON.parse(localStorage.getItem('lucky-search-freq') || '{}');
      const terms = Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 8).map((e) => e[0]);
      const display = terms.length >= 3 ? terms : TRENDING_FALLBACK;
      const arrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="10" height="10" style="opacity:0.5"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>';
      container.innerHTML = display.map((term) =>
        `<button class="search-tag search-tag--trending" data-search-term="${term.replace(/"/g, '&quot;')}">${term}${arrowSvg}</button>`
      ).join('');
      container.querySelectorAll('[data-search-term]').forEach((tag) => {
        on(tag, 'click', () => {
          const term = tag.dataset.searchTerm;
          if (input) input.value = term;
          searchProducts(term);
          saveSearch(term);
          trackFreq(term);
        });
      });
    } catch {}
  }

  window.LuckySearch = { open: openSearch, close: closeSearch };
}

/* ============================================================
   STICKY ADD-TO-CART (mobile)
   ============================================================ */
function initStickyAtc() {
  const stickyBar = $('#sticky-atc');
  const mainBtn = $('#product-add-btn');
  if (!stickyBar || !mainBtn) return;

  let scrolledPast = false;
  let variantInCart = false;

  function refreshStickyVisibility() {
    const show = scrolledPast && !variantInCart;
    stickyBar.classList.toggle('is-visible', show);
    stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    const stack = $('#fab-stack');
    if (stack) stack.classList.toggle('fab-stack--lifted', show);
  }

  function checkVariantInCart(variantId) {
    const state = Cart.getState();
    variantInCart = state && (state.items || []).some(
      (item) => String(item.variant_id) === String(variantId)
    );
    refreshStickyVisibility();
  }

  const observer = new IntersectionObserver(
    ([entry]) => {
      scrolledPast = !entry.isIntersecting;
      refreshStickyVisibility();
    },
    { threshold: 0 }
  );
  observer.observe(mainBtn);

  // Initial check
  const page = $('#product-page');
  if (page && page.dataset.defaultVariant) {
    Cart.fetchCart().then(() => checkVariantInCart(page.dataset.defaultVariant));
  }

  // Re-check when variant changes or item added
  document.addEventListener('lucky:variant-change', ({ detail }) => {
    checkVariantInCart(detail.variantId);
  });
}

/* ============================================================
   CART FLOATING BUTTON
   ============================================================ */
function initCartFab() {
  const fab = $('#cart-fab');
  if (!fab) return;

  function updateFab(count) {
    const show = count > 0;
    fab.classList.toggle('is-visible', show);
    const countEl = fab.querySelector('[data-cart-fab-count]');
    if (countEl) countEl.textContent = count;
  }

  Cart.fetchCart().then((cart) => updateFab(cart.item_count));
  document.addEventListener('lucky:cart-updated', ({ detail }) => {
    updateFab(detail.count || 0);
  });
}

/* ============================================================
   WISHLIST
   ============================================================ */
const Wishlist = (() => {
  const key = () => {
    const cid = (window.themeSettings && window.themeSettings.customerId) || 'guest';
    return `lb_wishlist_${cid}`;
  };
  const get = () => JSON.parse(localStorage.getItem(key()) || '[]');
  const save = (list) => {
    localStorage.setItem(key(), JSON.stringify(list));
    updateWishlistCount(list.length);
  };
  const toggle = (handle) => {
    const list = get();
    const idx = list.indexOf(handle);
    if (idx > -1) list.splice(idx, 1); else list.push(handle);
    save(list);
    return idx === -1;
  };
  const has = (handle) => get().includes(handle);
  return { get, toggle, has };
})();

function updateWishlistCount(count) {
  $$('[data-wishlist-count]').forEach((el) => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
  document.dispatchEvent(new CustomEvent('lucky:wishlist-updated', { detail: { count } }));
}

function initWishlist() {
  updateWishlistCount(Wishlist.get().length);

  // Mark already-wishlisted buttons
  $$('[data-wishlist-handle]').forEach((btn) => {
    if (Wishlist.has(btn.dataset.wishlistHandle)) {
      btn.classList.add('is-wishlisted');
      btn.setAttribute('aria-label', 'Remover dos favoritos');
      const textEl = btn.querySelector('.product-wishlist-btn__text');
      if (textEl) textEl.textContent = 'Favoritado';
    }
  });

  // Delegate clicks on wishlist buttons
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-handle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.wishlistHandle;
    const added = Wishlist.toggle(handle);
    btn.classList.toggle('is-wishlisted', added);
    btn.setAttribute('aria-label', added ? 'Remover dos favoritos' : 'Adicionar aos favoritos');
    const textEl = btn.querySelector('.product-wishlist-btn__text');
    if (textEl) textEl.textContent = added ? 'Favoritado' : 'Favoritos';
    showToast(added ? 'Adicionado aos favoritos!' : 'Removido dos favoritos');
    // On wishlist page: remove card when unfavorited
    if (!added) {
      const grid = $('#wishlist-grid');
      if (grid) grid.dispatchEvent(new CustomEvent('lucky:removed-from-wishlist', { detail: { handle } }));
    }
  });
}

function initWishlistPage() {
  const grid = $('#wishlist-grid');
  if (!grid) return;

  const subtitle = $('#wishlist-subtitle');
  const empty = $('#wishlist-empty');
  const toolbar = $('#wishlist-toolbar');
  const selectAllCheck = $('#wishlist-select-all');
  const selectedCountEl = $('#wishlist-selected-count');

  // Move action bar to body to prevent clipping by section wrapper
  const actionBar = $('#wishlist-action-bar');
  if (actionBar) document.body.appendChild(actionBar);

  const actionCount = $('#wishlist-action-count');
  const addCartBtn = $('#wishlist-add-cart');
  const checkoutBtn = $('#wishlist-checkout');

  const handles = Wishlist.get();
  const selected = new Map(); // handle → variantId

  function updateActionBar() {
    const count = selected.size;
    if (actionBar) actionBar.classList.toggle('is-visible', count > 0);
    if (actionCount) {
      actionCount.textContent = `${count} ${count === 1 ? 'produto selecionado' : 'produtos selecionados'}`;
    }
    if (selectedCountEl) {
      selectedCountEl.textContent = count > 0 ? `${count} selecionado${count > 1 ? 's' : ''}` : '';
    }
    const allChecks = grid.querySelectorAll('.wishlist-select-check');
    if (selectAllCheck) {
      selectAllCheck.checked = allChecks.length > 0 && count === allChecks.length;
      selectAllCheck.indeterminate = count > 0 && count < allChecks.length;
    }
  }

  function getSelectedItems() {
    return Array.from(selected.values())
      .filter(Boolean)
      .map((variantId) => ({ id: Number(variantId), quantity: 1 }));
  }

  function removeCard(handle) {
    const card = grid.querySelector(`.wishlist-card-wrap[data-handle="${handle}"]`);
    if (!card) return;
    card.style.transition = 'opacity 0.3s, transform 0.3s';
    card.style.opacity = '0';
    card.style.transform = 'scale(0.9)';
    setTimeout(() => {
      card.remove();
      selected.delete(handle);
      updateActionBar();
      const remaining = grid.querySelectorAll('.wishlist-card-wrap').length;
      if (subtitle) subtitle.textContent = `${remaining} ${remaining === 1 ? 'produto salvo' : 'produtos salvos'}`;
      if (remaining === 0 && empty) {
        grid.style.display = 'none';
        if (toolbar) toolbar.style.display = 'none';
        empty.style.display = 'flex';
      }
    }, 300);
  }

  if (handles.length === 0) {
    if (subtitle) subtitle.textContent = '0 produtos salvos';
    if (empty) empty.style.display = 'flex';
    return;
  }

  Promise.all(
    handles.map((handle) =>
      fetch(`/products/${handle}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null)
    )
  ).then((products) => {
    const valid = products.filter(Boolean);
    if (subtitle) subtitle.textContent = `${valid.length} ${valid.length === 1 ? 'produto salvo' : 'produtos salvos'}`;
    if (valid.length === 0) {
      if (empty) empty.style.display = 'flex';
      return;
    }

    if (toolbar) toolbar.style.display = 'flex';
    grid.style.display = 'grid';

    grid.innerHTML = valid.map((p) => {
      const variant = p.variants && p.variants[0];
      const price = variant ? formatMoney(variant.price / 100) : '';
      const comparePrice = variant && variant.compare_at_price > variant.price
        ? formatMoney(variant.compare_at_price / 100) : '';
      const img = p.featured_image
        ? `<img src="${p.featured_image}" alt="${p.title}" loading="lazy">`
        : '';
      const available = variant ? variant.available : false;
      const variantId = variant ? variant.id : '';
      return `
        <div class="wishlist-card-wrap" data-handle="${p.handle}" data-variant-id="${variantId}">
          <label class="wishlist-select-label" aria-label="Selecionar ${p.title}" onclick="event.stopPropagation()">
            <input type="checkbox" class="wishlist-select-check" data-handle="${p.handle}" data-variant-id="${variantId}">
            <span class="wishlist-select-box"></span>
          </label>
          <a href="/products/${p.handle}" class="product-card" style="min-width:0">
            <div class="product-card__image-wrap">
              ${img}
              <button class="product-card__wishlist is-wishlisted" data-wishlist-handle="${p.handle}" aria-label="Remover dos favoritos">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
                </svg>
              </button>
              ${!available ? '<span class="product-card__badge" style="background:var(--muted-foreground)">Esgotado</span>' : ''}
            </div>
            <div class="product-card__info">
              <h3 class="product-card__title">${p.title}</h3>
              <div class="product-card__price-wrap">
                ${comparePrice ? `<span class="product-card__compare">${comparePrice}</span>` : ''}
                <span class="product-card__price">${price}</span>
              </div>
            </div>
          </a>
        </div>`;
    }).join('');

    // Remove card when unfavorited on this page
    grid.addEventListener('lucky:removed-from-wishlist', (e) => removeCard(e.detail.handle));

    // Checkbox selection
    grid.addEventListener('change', (e) => {
      const check = e.target.closest('.wishlist-select-check');
      if (!check) return;
      if (check.checked) {
        selected.set(check.dataset.handle, check.dataset.variantId);
      } else {
        selected.delete(check.dataset.handle);
      }
      updateActionBar();
    });

    // Select all
    if (selectAllCheck) {
      selectAllCheck.addEventListener('change', () => {
        grid.querySelectorAll('.wishlist-select-check').forEach((check) => {
          check.checked = selectAllCheck.checked;
          if (selectAllCheck.checked) {
            selected.set(check.dataset.handle, check.dataset.variantId);
          } else {
            selected.delete(check.dataset.handle);
          }
        });
        updateActionBar();
      });
    }

    // Add to cart
    if (addCartBtn) {
      addCartBtn.addEventListener('click', async () => {
        const items = getSelectedItems();
        if (!items.length) return;
        addCartBtn.disabled = true;
        addCartBtn.textContent = 'Adicionando...';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });
          // Fetch cart to get full item data for tracking
          const cart = await Cart.fetchCart();
          document.dispatchEvent(new CustomEvent('lucky:cart-updated', { detail: { count: cart.item_count } }));
          const addedIds = new Set(items.map((i) => i.id));
          cart.items.filter((i) => addedIds.has(i.variant_id)).forEach((i) => LuckyTracking.addToCart(i));
          showToast(`${items.length} ${items.length === 1 ? 'produto adicionado' : 'produtos adicionados'} ao carrinho!`);
          const cartOpenBtn = document.querySelector('[data-cart-open]');
          if (cartOpenBtn) cartOpenBtn.click();
        } catch {
          showToast('Erro ao adicionar ao carrinho. Tente novamente.');
        }
        addCartBtn.disabled = false;
        addCartBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4zM3 6h18M16 10a4 4 0 01-8 0"/></svg> Adicionar ao carrinho';
      });
    }

    // Checkout
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', async () => {
        const items = getSelectedItems();
        if (!items.length) return;
        checkoutBtn.disabled = true;
        checkoutBtn.textContent = 'Aguarde...';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          });
          window.location.href = '/cart';
        } catch {
          showToast('Erro ao processar. Tente novamente.');
          checkoutBtn.disabled = false;
          checkoutBtn.textContent = 'Finalizar compra';
        }
      });
    }
  });
}

/* ============================================================
   PROMO POPUP
   ============================================================ */
function initPromoPopup() {
  const popup = $('#promo-popup-overlay');
  if (!popup) return;

  // Check localStorage cooldown
  const cooldownDays = parseInt(popup.dataset.cooldown || '7', 10);
  const storageKey = 'lucky-popup-dismissed';
  const stored = localStorage.getItem(storageKey);
  if (stored) {
    const dismissedAt = parseInt(stored, 10);
    const msPerDay = 86400000;
    if (Date.now() - dismissedAt < cooldownDays * msPerDay) return;
  }

  const delayMs = parseInt(popup.dataset.delay || '4', 10) * 1000;

  function closePopup() {
    popup.classList.add('is-hiding');
    localStorage.setItem(storageKey, String(Date.now()));
    setTimeout(() => { popup.style.display = 'none'; popup.classList.remove('is-hiding', 'is-visible'); }, 300);
  }

  setTimeout(() => {
    popup.style.display = 'flex';
    requestAnimationFrame(() => requestAnimationFrame(() => popup.classList.add('is-visible')));
  }, delayMs);

  on($('#promo-popup-close'), 'click', closePopup);
  on($('#promo-popup-skip'), 'click', closePopup);

  on($('#promo-popup-form'), 'submit', (e) => {
    e.preventDefault();
    const inputs = popup.querySelectorAll('.promo-popup__input');
    const name  = inputs[0]?.value.trim() || '';
    const email = inputs[1]?.value.trim() || '';
    const phone = inputs[2]?.value.trim() || '';
    // Send to Shopify customer newsletter via /contact form
    if (email) {
      fetch('/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          form_type: 'customer',
          utf8: '✓',
          'contact[email]': email,
          'contact[name]': name,
          'contact[phone]': phone,
          'contact[body]': 'Cadastro via popup promocional',
        }),
      }).catch(() => {});
    }
    // Show coupon code instead of closing
    const form = $('#promo-popup-form');
    const skipBtn = $('#promo-popup-skip');
    const couponEl = $('#promo-popup-coupon');
    if (form) form.style.display = 'none';
    if (skipBtn) skipBtn.textContent = 'Fechar';
    if (couponEl) couponEl.style.display = '';

    // Copy button
    const copyBtn = $('#promo-coupon-copy');
    const codeEl = $('#promo-coupon-code');
    if (copyBtn && codeEl) {
      on(copyBtn, 'click', () => {
        navigator.clipboard.writeText(codeEl.textContent.trim()).then(() => {
          copyBtn.textContent = 'Copiado!';
          setTimeout(() => { copyBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="14" height="14"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/></svg> Copiar'; }, 2000);
        });
      });
    }

    localStorage.setItem('lucky-popup-dismissed', String(Date.now()));
  });

  // Close on backdrop click
  on(popup, 'click', (e) => {
    if (e.target === popup) closePopup();
  });
}

/* ============================================================
   COMPRE MIX — JS-powered section
   ============================================================ */
function initCompreMix() {
  const section = $('#compre-mix');
  if (!section) return;

  const viewport = section.querySelector('[data-mix-viewport]');
  const container = section.querySelector('[data-mix-container]');
  const prevBtn = section.querySelector('[data-mix-prev]');
  const nextBtn = section.querySelector('[data-mix-next]');
  const totalEl = section.querySelector('[data-mix-total]');
  const countEl = section.querySelector('[data-mix-count]');
  const addAllBtn = section.querySelector('[data-mix-add-all]');

  if (!container) return;

  const pages = [...container.children];
  let currentPage = 0;

  function scrollToPage(idx) {
    if (!pages.length) return;
    currentPage = ((idx % pages.length) + pages.length) % pages.length;
    container.style.transform = `translateX(-${currentPage * 100}%)`;
  }

  on(prevBtn, 'click', () => scrollToPage(currentPage - 1));
  on(nextBtn, 'click', () => scrollToPage(currentPage + 1));

  scrollToPage(0);

  // Product selection
  const selectedVariants = {};
  let total = 0;

  $$('[data-mix-card]', section).forEach((card) => {
    const variantId = card.dataset.variantId;
    const price = parseFloat(card.dataset.price || '0');
    let selected = card.dataset.selected === 'true';

    selectedVariants[variantId] = selected;

    updateCard(card, selected);

    on(card, 'click', (e) => {
      if (e.target.tagName === 'SELECT') return;
      selected = !selected;
      selectedVariants[variantId] = selected;
      updateCard(card, selected);
      recalcTotal();
    });

    const sel = card.querySelector('select[data-variant-select]');
    if (sel) {
      on(sel, 'change', (e) => {
        e.stopPropagation();
        card.dataset.variantId = sel.value;
        card.dataset.price = sel.options[sel.selectedIndex].dataset.price;
        recalcTotal();
      });
    }
  });

  function updateCard(card, isSelected) {
    const checkbox = card.querySelector('[data-mix-checkbox]');
    const checkmark = card.querySelector('[data-mix-checkmark]');
    card.classList.toggle('is-selected', isSelected);
    if (checkbox) checkbox.classList.toggle('is-checked', isSelected);
    if (checkmark) checkmark.style.display = isSelected ? '' : 'none';
  }

  function recalcTotal() {
    let sum = 0;
    let count = 0;
    $$('[data-mix-card]', section).forEach((card) => {
      const vid = card.dataset.variantId;
      if (selectedVariants[vid]) {
        sum += parseFloat(card.dataset.price || '0');
        count++;
      }
    });
    if (totalEl) totalEl.textContent = formatMoney(sum / 100);
    if (countEl) countEl.textContent = `${count} ${count === 1 ? 'peça selecionada' : 'peças selecionadas'}`;
    if (addAllBtn) {
      addAllBtn.disabled = count === 0;
      const label = addAllBtn.querySelector('[data-mix-btn-label]');
      if (label) label.textContent = `Adicionar ${count} ${count === 1 ? 'Peça' : 'Peças'}`;
    }
  }

  recalcTotal();

  on(addAllBtn, 'click', async () => {
    const toAdd = $$('[data-mix-card].is-selected', section);
    if (!toAdd.length) { showToast('Selecione ao menos uma peça!', 'error'); return; }

    addAllBtn.disabled = true;
    const original = addAllBtn.innerHTML;
    addAllBtn.innerHTML = '<svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="16" height="16"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>';

    try {
      for (const card of toAdd) {
        const item = await Cart.addItem({ variantId: card.dataset.variantId, quantity: 1 });
        if (item) LuckyTracking.addToCart(item);
      }
      showToast(`${toAdd.length} ${toAdd.length === 1 ? 'peça adicionada' : 'peças adicionadas'} à sacola! 🛍️`);
      if (window.LuckyCart) window.LuckyCart.open();
    } catch {
      showToast('Erro ao adicionar. Tente novamente.', 'error');
    } finally {
      addAllBtn.disabled = false;
      addAllBtn.innerHTML = original;
      recalcTotal();
    }
  });
}


/* ============================================================
   TOAST NOTIFICATIONS
   ============================================================ */
let toastTimer;
function showToast(message, type = 'success') {
  let toast = $('#lucky-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'lucky-toast';
    toast.style.cssText = `
      position:fixed;top:1rem;left:50%;transform:translateX(-50%);
      z-index:100;padding:0.75rem 1.5rem;border-radius:var(--radius);
      font-family:'Montserrat',sans-serif;font-size:13px;font-weight:500;
      box-shadow:0 8px 30px -4px rgba(0,0,0,0.2);
      transition:opacity 0.3s;pointer-events:none;white-space:nowrap;
    `;
    document.body.appendChild(toast);
  }

  toast.style.background = type === 'error' ? 'hsl(0,72%,50%)' : 'hsl(25,30%,14%)';
  toast.style.color = '#fff';
  toast.textContent = message;
  toast.style.opacity = '1';

  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.style.opacity = '0'; }, 3000);
}

/* ============================================================
   BOOT
   ============================================================ */
function initFabs() {
  const stack = $('#fab-stack');
  const footer = document.querySelector('.site-footer');
  if (!stack) return;

  const whatsappFab = $('#whatsapp-fab');
  const wishlistFab = $('#wishlist-fab');

  let nearFooter = false;
  let scrolled = false;
  let hasWishlistItems = Wishlist.get().length > 0;

  function update() {
    const show = scrolled && !nearFooter;
    stack.classList.toggle('fab-stack--hidden', !show);
    if (whatsappFab) whatsappFab.classList.toggle('is-visible', show);
    if (wishlistFab) wishlistFab.classList.toggle('is-visible', show && hasWishlistItems);
  }

  // Re-evaluate wishlist FAB when wishlist changes
  document.addEventListener('lucky:wishlist-updated', ({ detail }) => {
    hasWishlistItems = detail.count > 0;
    update();
  });

  window.addEventListener('scroll', () => {
    scrolled = window.scrollY > 200;
    update();
  }, { passive: true });

  if (footer) {
    const observer = new IntersectionObserver(
      ([entry]) => {
        nearFooter = entry.isIntersecting;
        update();
      },
      { rootMargin: '0px 0px 0px 0px', threshold: 0.05 }
    );
    observer.observe(footer);
  }
}

/* ============================================================
   HERO PARALLAX
   ============================================================ */
function initHeroParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const hero = document.querySelector('.hero-carousel');
  if (!hero) return;

  // Cache heroH to avoid forced reflow on every scroll event
  let heroH = hero.offsetHeight;
  window.addEventListener('resize', () => { heroH = hero.offsetHeight; }, { passive: true });

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    requestAnimationFrame(() => {
      const scrolled = window.scrollY;
      if (scrolled > heroH * 1.2) { ticking = false; return; }
      const offset = scrolled * 0.28;
      hero.querySelectorAll('.hero-carousel__slide.is-active picture, .hero-carousel__slide.is-active > img').forEach((el) => {
        el.style.transform = `translateY(${offset}px)`;
      });
      ticking = false;
    });
    ticking = true;
  }, { passive: true });
}

document.addEventListener('DOMContentLoaded', () => {
  initFadeInSections();
  initHeroParallax();
  initStickyHeader();
  initFabs();
  initMobileMenu();
  initScrollCarousels();
  initFooterAccordion();
  initCartDrawer();
  initAddToCartButtons();
  initProductPage();
  initGalleryLightbox($('#pers-page'), '.pers-thumb.is-active');
  initCollectionPage();
  initSearch();
  initStickyAtc();
  initCartFab();
  initWishlist();
  initWishlistPage();
  initPromoPopup();
  initCompreMix();

  // Expose add-to-cart globally so product card onclick attributes work
  window.LuckyAddToCart = addToCartHandler;

  // Document-level delegation: cart + search open/close
  // This handles clicks on SVG child elements inside buttons correctly
  document.addEventListener('click', (e) => {
    const cartOpen = e.target.closest('[data-cart-open]');
    const cartClose = e.target.closest('[data-cart-close]');
    const searchOpen = e.target.closest('[data-search-open]');
    const searchClose = e.target.closest('[data-search-close]');
    const cartOverlay = document.getElementById('cart-overlay');

    if (cartOpen) {
      e.preventDefault();
      window.LuckyCart?.open();
    } else if (cartClose) {
      window.LuckyCart?.close();
    } else if (searchOpen) {
      e.preventDefault();
      window.LuckySearch?.open();
    } else if (searchClose) {
      window.LuckySearch?.close();
    } else if ((cartOverlay && e.target === cartOverlay) || e.target.id === 'cart-backdrop') {
      // Backdrop click closes cart
      window.LuckyCart?.close();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      window.LuckyCart?.close();
      window.LuckySearch?.close();
    }
  });
});

// Shopify Theme Editor: re-observe reveal elements when a section is added
document.addEventListener('shopify:section:load', (e) => {
  const newEls = e.target.querySelectorAll('.fade-in-section, [data-reveal]');
  if (!newEls.length) return;
  // Make visible immediately in editor to avoid blank sections
  newEls.forEach((el) => el.classList.add('is-visible'));
});
